import { useEffect } from 'react';
import { defaultTheme, getTheme, getThemeRows, setTheme, themeGroups } from './themes';

// Text being written keeps its keys
const writing = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

// A key press makes the browser ring the control last clicked (the theme picker, say); the theme keys let go of a clicked control so
// nothing lights up, but keep focus that was reached with the keyboard
let focusFromPointer = false;
const letGoOfFocus = () => {
  if (focusFromPointer && document.activeElement instanceof HTMLElement && document.activeElement !== document.body) document.activeElement.blur();
};

/** ] goes to the next theme and [ to the one before, in the theme list's order; with Shift, [ goes straight to the default light theme and ] to the default dark one. */
export function ThemeCycleKeys() {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.repeat || writing(event.target)) return;
      // With Shift the bracket keys type { and }
      if (event.key === '{' || event.key === '}') {
        event.preventDefault();
        letGoOfFocus();
        setTheme(defaultTheme(event.key === '{' ? 'light' : 'dark').key);
        return;
      }
      if (event.key !== '[' && event.key !== ']') return;
      event.preventDefault();
      letGoOfFocus();
      const order = themeGroups(getThemeRows()).flatMap(group => group.themes);
      const at = order.findIndex(theme => theme.key === getTheme());
      const next = order[(at + (event.key === ']' ? 1 : order.length - 1)) % order.length];
      if (next) setTheme(next.key);
    };
    const onPointer = () => (focusFromPointer = true);
    const onTab = (event: KeyboardEvent) => {
      if (event.key === 'Tab') focusFromPointer = false;
    };
    window.addEventListener('pointerdown', onPointer, true);
    window.addEventListener('keydown', onTab, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onPointer, true);
      window.removeEventListener('keydown', onTab, true);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  return null;
}
